<template>
  <div lang="de">
    <div class="wrapper" :class="{ 'nav-open': $sidebar.showSidebar }" data-background-color="white">
      <SidebarPluginSideBar :background-color="sidebarBackground" :short-title="$t('sidebaradmin.shortTitle')"
        :title="$t('sidebaradmin.title')">
        <template #links="props">
          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.messages'),
            icon: 'fas fa-bell',
            path: '/dashboard/aktuelles'
          }">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.knowledge'),
            icon: 'fa-solid fa-book-open-cover',
            path: '/dashboard/knowledge'
          }">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.mydatabases'),
            materialIcon: 'dashboard',
            path: '/dashboard/meine-datenbanken'
          }">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.settings'),
            icon: 'fas fa-user-cog',
            path: '/dashboard/einstellungen'
          }">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.mysubscriptions'),
            icon: 'fa-solid fa-arrows-repeat',
            path: '/dashboard/meineabos'
          }">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.shopping'),
            icon: 'fa fa-shopping-cart',
            path: '/produkte/shop/basket'
          }" v-if="shopAvailableForUser">
          </SidebarPluginSidebarItem>

          <!--ADMIN-->
          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.templates'),
            materialIcon: 'dashboard',
            path: '/dashboard/datenbank-templates'
          }" v-if="superAdmin">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.verwaltung'),
            materialIcon: 'manage_accounts',
            path: '/dashboard/verwaltung'
          }" v-if="superAdmin">
          </SidebarPluginSidebarItem>

          <SidebarPluginSidebarItem :link="{
            name: $t('sidebaradmin.product'),
            icon: 'fa-solid fa-tags',
            path: '/dashboard/produktverwaltungen'
          }" v-if="superAdmin">
          </SidebarPluginSidebarItem>

          <p class="d-lg-none cursor-pointer font-weight-400 text-color-white"
            style="margin-left: 24px; margin-top: 20px;" @click="accountLogOut()">
            Abmelden</p>

          <div class="cursor-pointer" v-if="superAdmin && !!versionDataBackend && !!versionDataFrontend"
            style="opacity: 0.85; margin-left: 25px; margin-top: 20px; margin-bottom: 10px"
            @click="furtherVersionInfos = !furtherVersionInfos">
            <small class="text-color-white">
              <strong>Frontend</strong><br>
              v. {{ versionDataFrontend.version }} ({{ versionDataFrontend.buildNumber }})<br>
              <span v-if="furtherVersionInfos">
                Deploy: {{ $d(versionDataFrontend.buildTime, 'medium') }}<br>
                SCM: {{ versionDataFrontend.gitCommit }} ({{ versionDataFrontend.gitBranch }})<br>
              </span>
              <strong>Backend</strong><br>
              v. {{ versionDataBackend.version }} ({{ versionDataBackend.buildNumber }})<br>
              <span v-if="furtherVersionInfos">
                Deploy: {{ $d(versionDataBackend.buildTime, 'medium') }}<br>
                SCM: {{ versionDataBackend.gitCommit }} ({{ versionDataBackend.gitBranch }})<br>
              </span>
            </small>
          </div>

        </template>
      </SidebarPluginSideBar>

      <div class="main-panel">

        <LayoutDashboardNavbar></LayoutDashboardNavbar>

        <router-view name="header"></router-view>

        <GracePeriodBanner v-if="$authStore.isCurrentUserInGracePeriod" />

        <div class="content" style="padding-top: 35px; background-color: white; min-height: 95vh" @click="toggleSidebar">
          <ElConfigProvider :locale="DE">
            <NuxtPage></NuxtPage>
          </ElConfigProvider>
        </div>
      </div>

    </div>
  </div>
</template>
<script lang="ts" setup>
  import PerfectScrollbar from 'perfect-scrollbar';
  import 'perfect-scrollbar/css/perfect-scrollbar.css';
  import DE from 'element-plus/dist/locale/de.min.mjs';
  import {
    APP_BUILD_NUMBER,
    APP_BUILD_TIME,
    APP_GIT_BRANCH,
    APP_GIT_COMMIT,
    APP_VERSION,
  } from '~/modules/common/services/util/buildnumber';
  import { ServerInformation } from '~/api-client';
  import { useAuthStore } from '~/modules/common/stores/auth';
  import { useSidebar } from '~/modules/common/stores/sidebar';
  import { useSubscriptionStore } from '~/store/subscriptions';

  const {$tenant, $subscriptionStore} = useNuxtApp();
function hasElement(className: string) {
  return document.getElementsByClassName(className).length > 0;
}

function initializeScrollbar(className: string) {
  if (hasElement(className)) {
    new PerfectScrollbar(`.${className}`);
  } else {
    // try to init it later in case this component is loaded async
    setTimeout(() => {
      initializeScrollbar(className);
    }, 100);
  }
}

const { $backendService } = useNuxtApp();
const $sidebar = useSidebar();
const authStore = useAuthStore();

const sidebarBackground = ref('vue');
const furtherVersionInfos = ref(false);
const versionDataFrontend = ref({
  version: APP_VERSION,
  buildNumber: APP_BUILD_NUMBER,
  buildTime: new Date(APP_BUILD_TIME),
  gitCommit: APP_GIT_COMMIT.substring(0, 6),
  gitBranch: APP_GIT_BRANCH
});

const admin = computed<boolean>(() => authStore.isCurrentUserAdmin === true);
const dbOwner = computed<boolean>(() => authStore.isCurrentUserDbOwner === true);
const superAdmin = computed<boolean>(() => authStore.isCurrentUserSuperAdmin === true);

const { data: versionDataBackend } = useLazyAsyncData(async () => {
  let version: ServerInformation;

  try {
    version = await $backendService.infoControllerApi.getInfo();
  } catch (e) {
    version = {
      version: '0.0.0',
      buildNumber: '?',
      buildTime: 0,
      gitCommit: '?',
      gitBranch: '?'
    }
  }

  return {
    ...version,
    buildTime: new Date(version.buildTime! * 1000),
    gitCommit: version.gitCommit?.substring(0, 6)
  };
});

const toggleSidebar = () => $sidebar.showSidebar && $sidebar.displaySidebar(false);
const initScrollbar = () => {
  let docClasses = document.body.classList;
  let isWindows = navigator.platform.startsWith('Win');
  if (isWindows) {
    // if we are on windows OS we activate the perfectScrollbar function
    initializeScrollbar('sidebar');
    initializeScrollbar('main-panel');
    initializeScrollbar('sidebar-wrapper');

    docClasses.add('perfect-scrollbar-on');
  } else {
    docClasses.add('perfect-scrollbar-off');
  }
};

const shopAvailableForUser = ref<boolean>(!$tenant.isMyDb);

const specificTenantRequirement = () => {
  if ($tenant.isMyDb){
    shopAvailableForUser.value = !$subscriptionStore.shopNotAvailableForCurrentUser && !authStore.isCurrentUserInactive;
  } else {
    shopAvailableForUser.value = true;
  }
}

watch(() => [$subscriptionStore.shopNotAvailableForCurrentUser, authStore.isCurrentUserInactive], (newVal) => {
  shopAvailableForUser.value = !$subscriptionStore.shopNotAvailableForCurrentUser && !authStore.isCurrentUserInactive;
});

const accountLogOut = async () => await useAuth().signOut({ callbackUrl: '/' });

const route = useRoute();
watch(() => route.fullPath, () => {
  const mainPanel = document.querySelector('.main-panel');
  if (mainPanel) mainPanel.scrollTop = 0;
  window.scrollTo(0, 0);
});

onMounted(async () => {
  initScrollbar();
  await useSubscriptionStore().getCurrentUserSubscriptions().then(() => {
    specificTenantRequirement();
  });

});
</script>
<style lang="scss">
@media (max-width: 600px) {
  .logo-mobile {
    margin-right: -25px !important;
  }
}
</style>
